/**
 * Project Bobcat — the five-stage nav (Design/Build/Validate/Deploy/Observe)
 * that replaces the single "Agent Workflow" header tab, per the real
 * Protolab prototype (`prostudio-build-flow-layouts.html`, checked live —
 * see "PR Spec: 5-Stage Nav"). Phase 1 only: this module is the state
 * machine for WHICH stage is selected. Four of the five stages
 * (`design`/`validate`/`deploy`/`observe`) render an honest placeholder for
 * now — their real content is separately-phased work. `build` renders
 * today's real Table View/Test View/Canvas, unchanged.
 *
 * All five stages share the single existing `/workflows` route rather than
 * getting their own routes — the same "component state, not a URL segment"
 * choice `workflows-view-mode.ts` already made for Table/Test/Canvas, kept
 * for consistency and because a routing overhaul is explicitly out of scope
 * for this phase. `tableStore.activeStage` is the cross-component bridge:
 * the header (`PrimaryNav`, via `StageNav`) writes it, `builder.tsx` reads
 * it — the two live in different parts of the component tree.
 */
export type WorkflowStage =
  | 'design'
  | 'build'
  | 'validate'
  | 'deploy'
  | 'observe';

/** The stage the page falls back to. Real, shipped content — always reachable. */
export const DEFAULT_STAGE: WorkflowStage = 'build';

/**
 * Parse the string a `SegmentedControl` change event carries. Same discipline
 * as `parseViewMode`: a real parse, not a cast, so an unrecognised value
 * falls back instead of the control throwing.
 */
export const parseStage = (value: string): WorkflowStage => {
  switch (value) {
    case 'design':
    case 'build':
    case 'validate':
    case 'deploy':
    case 'observe':
      return value;
    default:
      return DEFAULT_STAGE;
  }
};

/**
 * The stage the page should actually render, given whether the five-stage
 * nav is even on. Mirrors `resolveEffectiveViewMode`'s reasoning: `stage` is
 * component state and outlives the flag — a user parked on `design` when
 * `bobcat-five-stage-nav` is switched off (or hasn't answered yet) must not
 * be left on a stage its own nav control no longer renders.
 */
export const resolveEffectiveStage = (
  stage: WorkflowStage,
  fiveStageNavEnabled: boolean,
  closed: readonly WorkflowStage[] = []
): WorkflowStage =>
  fiveStageNavEnabled && !closed.includes(stage) ? stage : DEFAULT_STAGE;

/**
 * The stages whose own flag is off (PSTUDIO-4583). The nav shows them grayed
 * out as "coming soon", and `resolveEffectiveStage` sends a stored or
 * deep-linked one to Build. Validate follows `bobcat-validate-stage` alone:
 * per the release page that flag also covers Test View.
 */
export const closedStages = ({
  validate,
  observe,
}: {
  validate: boolean;
  observe: boolean;
}): WorkflowStage[] => [
  ...(validate ? [] : (['validate'] as const)),
  ...(observe ? [] : (['observe'] as const)),
];
